import { useId, useState } from 'react';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { useTranslation } from '../context/TranslationContext';
import useShippingConfig, { formatCitiesForDisplay } from '../hooks/useShippingConfig';
import './AddressSearch.css';

const provider = new OpenStreetMapProvider({
  params: {
    countrycodes: 'ch', // Restrict exclusively to Switzerland
    addressdetails: 1,
  },
});

export default function AddressSearch({ onSelectResult, showDeliveryDisclaimer = true }) {
  const { t } = useTranslation();
  const { cities } = useShippingConfig();
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setActiveIndex(-1);
    if (query.length > 3) {
      const results = await provider.search({ query });
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelect = (result) => {
    const details = result.raw?.address || {};

    const streetCandidates = [
      details.road, details.pedestrian, details.footway, details.street,
      details.suburb, details.hamlet, details.neighbourhood,
      details.locality, details.residential,
    ].filter(Boolean);

    const streetName = streetCandidates[0] || '';
    const houseNumber = details.house_number || '';
    const street = streetName
      ? houseNumber ? `${streetName} ${houseNumber}` : streetName
      : result.label.split(',')[0].trim();

    const line2Candidates = [
      details.locality, details.residential,
      details.suburb, details.neighbourhood, details.hamlet,
      details.borough, details.quarter, details.city_district,
      details.district, details.isolated_dwelling, details.farm,
    ].filter(part => part && part !== streetName);

    const line2 = [...new Set(line2Candidates)].join(', ');

    onSelectResult({
      address_line1: street,
      address_line2: line2,
      city: details.city || details.town || details.village || details.municipality || '',
      postal_code: details.postcode || '',
      country: 'Switzerland',
    });

    setSearchResults([]);
    setSearchQuery('');
    setActiveIndex(-1);
  };

  const handleResultKeyDown = (e, result) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect(result);
    }
  };

  const handleInputKeyDown = (e) => {
    if (searchResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(searchResults[activeIndex]);
    } else if (e.key === 'Escape') {
      setSearchResults([]);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="address-search-container">
      {showDeliveryDisclaimer ? (
        <div className="delivery-disclaimer" role="status" aria-live="polite">
          {(() => {
            const citiesList = formatCitiesForDisplay(cities);
            return citiesList
              ? t('deliveryDisclaimerWithList', 'We currently deliver to: {citiesList}.').replace('{citiesList}', citiesList)
              : t('deliveryDisclaimerEmpty', 'Delivery is currently unavailable in your area.');
          })()}
        </div>
      ) : null}
      <label className="sr-only" htmlFor={inputId}>
        {t('searchAddressLabel', 'Search address in Switzerland')}
      </label>
      <input
        id={inputId}
        type="text"
        name="address-search"
        autoComplete="off"
        spellCheck={false}
        className="address-search-input"
        placeholder={t('searchAddress', 'Type to search for an address in Switzerland...')}
        value={searchQuery}
        onChange={handleSearch}
        onKeyDown={handleInputKeyDown}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={searchResults.length > 0}
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        aria-label={t('searchAddressLabel', 'Search address in Switzerland')}
      />
      {searchResults.length > 0 && (
        <ul
          id={listboxId}
          className="address-search-results"
          role="listbox"
          aria-label={t('addressSearchResultsLabel', 'Address suggestions')}
        >
          {searchResults.map((result, idx) => {
            const optionId = `${listboxId}-option-${idx}`;
            const isActive = idx === activeIndex;
            return (
              <li
                key={idx}
                id={optionId}
                role="option"
                aria-selected={isActive}
                tabIndex={0}
                onClick={() => handleSelect(result)}
                onKeyDown={(e) => handleResultKeyDown(e, result)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={isActive ? 'address-search-result-active' : undefined}
              >
                {result.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}